import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  SuperFund, 
  SuperContribution,
  PaginationParams 
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch, 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const SUPER_FUNDS_KEY = 'super-funds';
const SUPER_CONTRIBUTIONS_KEY = 'super-contributions';

// ==================== Super Funds ====================

export function useSuperFunds(params?: { search?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: SuperFund[]; meta: { total: number } }>(
    SUPER_FUNDS_KEY,
    '/payroll/super/funds',
    {
      page: params?.page || 1,
      limit: params?.limit || 50,
      search: params?.search,
    },
    {}
  );
}

export function useSuperFund(id: string) {
  return useFetch<SuperFund>(
    [SUPER_FUNDS_KEY, id],
    `/payroll/super/funds/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useCreateSuperFund() {
  const queryClient = useQueryClient();
  
  return useMutation<SuperFund, Error, Partial<SuperFund>>({
    mutationFn: (data) => apiClient.post('/payroll/super/funds', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_FUNDS_KEY] });
    },
  });
}

export function useUpdateSuperFund() {
  const queryClient = useQueryClient();
  
  return useMutation<SuperFund, Error, { id: string; data: Partial<SuperFund> }>({
    mutationFn: ({ id, data }) => apiClient.put(`/payroll/super/funds/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SUPER_FUNDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SUPER_FUNDS_KEY, variables.id] });
    },
  });
}

export function useDeleteSuperFund() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/payroll/super/funds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_FUNDS_KEY] });
    },
  });
}

// ==================== Super Contributions ====================

export function useSuperContributions(params?: { status?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: SuperContribution[]; meta: { total: number } }>(
    SUPER_CONTRIBUTIONS_KEY,
    '/payroll/super/contributions',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
    }
  );
}

export function useSuperContribution(id: string) {
  return useFetch<SuperContribution>(
    [SUPER_CONTRIBUTIONS_KEY, id],
    `/payroll/super/contributions/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useCreateSuperContribution() {
  const queryClient = useQueryClient();
  
  return useMutation<SuperContribution, Error, { payRunId: string }>({
    mutationFn: (data) => apiClient.post('/payroll/super/contributions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_CONTRIBUTIONS_KEY] });
    },
  });
}

export function useSubmitToSuperStream() {
  const queryClient = useQueryClient();
  
  return useMutation<SuperContribution, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/super/contributions/${id}/submit`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [SUPER_CONTRIBUTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SUPER_CONTRIBUTIONS_KEY, id] });
    },
  });
}

export function useDownloadSuperContributionXML() {
  return useMutation<Blob, Error, string>({
    mutationFn: (id) => apiClient.get(`/payroll/super/contributions/${id}/download`, { responseType: 'blob' }),
  });
}
